import React, { useState, useEffect } from 'react'; // Added useEffect for potential future use
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
// Removed unused 'doc', 'setDoc' imports as submission goes through context/cloud function
import { storage, db } from '../firebase/firebase';

// --- Constants & Configuration ---
const STAFF_ROLES = [
    { value: 'educator', label: 'Educator (Teaching & Content)' },
    { value: 'examiner', label: 'Examiner (Test Grading)' },
    { value: 'admin', label: 'Admin (Application for Review)' },
];

const MAX_FILE_SIZE_MB = 2;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// Utility function to generate a temporary application ID for logging/tracking if needed
const generateTempId = () => {
    return 'APP-' + Math.random().toString(36).substring(2, 9).toUpperCase();
};

// --- Main Component ---
export default function StaffApplication() {
    // --- Context and Navigation ---
    // Ensure `submitPublicApplication`, `isAcceptingStaff`, `settingsLoading` are provided by AuthContext
    const { submitPublicApplication, isAcceptingStaff, settingsLoading } = useAuth();
    const navigate = useNavigate();

    // --- State for Application Fields ---
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [dob, setDob] = useState('');
    const [role, setRole] = useState(STAFF_ROLES[0].value);
    const [address, setAddress] = useState('');
    const [qualifications, setQualifications] = useState('');
    const [medicalHistory, setMedicalHistory] = useState(''); // Optional field

    // --- File Upload States ---
    const [idProof, setIdProof] = useState(null);
    const [qualDoc, setQualDoc] = useState(null);

    // --- UI/Flow States ---
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    // --- File Upload Validation & Handler ---
    const handleFileChange = (e, setFileState) => {
        setError(''); // Clear previous errors on new selection
        const file = e.target.files[0];
        if (!file) {
            setFileState(null);
            e.target.value = null; // Clear input visually
            return;
        }

        // Validate file type (optional but recommended)
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
            setError('Invalid file type. Please upload PDF, JPG, or PNG.');
            setFileState(null);
            e.target.value = null;
            return;
        }

        // Validate file size
        if (file.size > MAX_FILE_SIZE_BYTES) {
            setError(`File size exceeds the ${MAX_FILE_SIZE_MB}MB limit. Please compress or choose a smaller file.`);
            setFileState(null);
            e.target.value = null;
            return;
        }

        // If valid, update state
        setFileState(file);
    };

    // --- Uploads file securely to Storage and returns URL ---
    // Note: Ensure Firebase Storage rules allow writes to these paths
    // and enforce the size limit server-side for security.
    // Example Rule for staff_id_proofs:
    // match /staff_id_proofs/{fileName} {
    //   allow write: if request.resource.size < 2 * 1024 * 1024
    //                && request.resource.contentType.matches('image/.*|application/pdf');
    // }
    const uploadFile = async (file, folder) => {
        if (!file) return null;
        // Using a timestamp and random element ensures more uniqueness than just filename
        const uniqueFileName = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}-${file.name}`;
        const storageRef = ref(storage, `${folder}/${uniqueFileName}`);

        try {
            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);
            console.log(`File uploaded to ${folder}: ${downloadURL}`); // Log success
            return downloadURL;
        } catch (uploadError) {
            console.error(`Error uploading file to ${folder}:`, uploadError);
            // Provide a more specific error based on the Firebase Storage error code if possible
            throw new Error(`Failed to upload ${file.name}. Please check your connection and try again. (${uploadError.code})`);
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Basic form validation
        if (!idProof || !qualDoc) {
             setError("Please upload both ID Proof and Qualification Proof documents.");
             return; // Stop submission if files are missing
        }
        if (!name || !email || !phone || !dob || !address || !qualifications) {
            setError("Please fill in all required fields.");
            return; // Stop submission if text fields missing
        }

        setLoading(true);

        try {
            // 1. Upload Documents concurrently
            console.log("Uploading ID proof...");
            const idUploadPromise = uploadFile(idProof, `staff_id_proofs`);
            console.log("Uploading qualification document...");
            const qualUploadPromise = uploadFile(qualDoc, `staff_qual_docs`);

            const [idProofURL, qualDocURL] = await Promise.all([idUploadPromise, qualUploadPromise]);

            // Ensure uploads completed successfully
            if (!idProofURL || !qualDocURL) {
                throw new Error("One or more document uploads failed. Please try again.");
            }
            console.log("Documents uploaded successfully.");

            // 2. Prepare Data for Cloud Function
            // This object structure should match what your 'submitOnlineApplication' cloud function expects
            const applicationData = {
                // Personal Info
                name: name.trim(),
                email: email.trim().toLowerCase(), // Normalize email
                phone: phone.trim(),
                dob: dob, // Assuming YYYY-MM-DD format from input type="date"
                address: address.trim(),
                // Application Details
                role: role, // Selected role value
                qualifications: qualifications.trim(),
                medicalHistory: medicalHistory.trim() || null, // Send null if empty
                // Document URLs
                idProofURL: idProofURL,
                qualDocURL: qualDocURL,
                // --- No need to set status, appliedAt, tempId here ---
                // The Cloud Function should set these server-side for consistency and security
            };

            console.log("Submitting application data:", applicationData);

            // 3. Submit Data via AuthContext -> Cloud Function
            // Ensure `submitPublicApplication` exists in AuthContext and calls your cloud function
            const result = await submitPublicApplication(applicationData);

            console.log("Submission successful:", result);
            setSuccess(result.message || "Application submitted successfully! We will review it shortly."); // Display success

            // 4. Clear form after success
            setName(''); setEmail(''); setPhone(''); setDob(''); setAddress(''); setQualifications(''); setMedicalHistory('');
            setIdProof(null); setQualDoc(null);
            // Reset file input fields visually
            document.querySelectorAll('input[type="file"]').forEach(input => input.value = null);


        } catch (error) {
            console.error('Error during staff application submission:', error);
            // Display specific error from upload or submission, or a generic one
            setError(`Submission Failed: ${error.message || 'An unexpected error occurred. Please try again.'}`);
        } finally {
            setLoading(false); // Ensure loading is turned off
        }
    };

    // --- Conditional Access Check ---
    if (settingsLoading) {
        return <div className="min-h-screen flex items-center justify-center text-lg text-gray-500">Checking portal status...</div>;
    }

    if (!isAcceptingStaff) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="bg-white shadow-xl rounded-lg p-8 md:p-10 w-full max-w-md border-t-4 border-yellow-500 text-center">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-yellow-800 mb-4">🔔 Applications Currently Closed</h2>
                    <p className="text-gray-700 text-base md:text-lg">
                        Thank you for your interest. We are not actively reviewing applications for staff positions at this time. Please check back later.
                    </p>
                    <button onClick={() => navigate('/')} className="mt-6 bg-indigo-600 text-white py-2 px-5 rounded-md hover:bg-indigo-700 transition duration-150">
                        Return to Portal Home
                    </button>
                </div>
            </div>
        );
    }
    // --- END Conditional Access Check ---

    // --- Render Form ---
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 py-10 px-4">
            <div className="bg-white shadow-xl rounded-lg p-8 md:p-10 w-full max-w-3xl border border-gray-200">

                <div className="text-center mb-8">
                    <h2 className="text-3xl font-extrabold text-indigo-700">Staff Application</h2>
                    <p className="text-gray-600 mt-2">Submit your details for review.</p>
                </div>

                {/* Display Success/Error Messages */}
                {error && <div className="text-red-700 bg-red-100 p-3 rounded-md mb-6 border border-red-300 text-sm font-medium">{error}</div>}
                {success && <div className="text-green-700 bg-green-100 p-3 rounded-md mb-6 border border-green-300 text-sm font-medium">{success}</div>}

                <form onSubmit={handleSubmit} className="space-y-5">

                    {/* Personal Details Section */}
                    <fieldset className="space-y-4">
                        <legend className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Personal & Role Details</legend>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                             <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                                <input id="name" type="text" placeholder="Your Full Name" value={name} onChange={(e) => setName(e.target.value)}
                                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" required />
                            </div>
                             <div>
                                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">Applying For Role *</label>
                                <select id="role" value={role} onChange={(e) => setRole(e.target.value)}
                                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white" required >
                                    {STAFF_ROLES.map(r => (<option key={r.value} value={r.value}>{r.label}</option>))}
                                </select>
                             </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                             <div className="md:col-span-2">
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                                <input id="email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
                                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" required />
                            </div>
                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                                <input id="phone" type="tel" placeholder="e.g., 9876543210" value={phone} onChange={(e) => setPhone(e.target.value)}
                                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" required />
                             </div>
                        </div>
                        <div>
                            <label htmlFor="dob" className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                            <input id="dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" required />
                        </div>
                        <div>
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">Full Residential Address *</label>
                            <input id="address" type="text" placeholder="Your Complete Address" value={address} onChange={(e) => setAddress(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" required />
                        </div>
                    </fieldset>

                    {/* Qualifications Section */}
                     <fieldset className="space-y-4 pt-4">
                        <legend className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Qualifications & History</legend>
                         <div>
                            <label htmlFor="qualifications" className="block text-sm font-medium text-gray-700 mb-1">Relevant Qualifications & Experience *</label>
                            <textarea id="qualifications" placeholder="List degrees, certifications, past roles, etc." value={qualifications} onChange={(e) => setQualifications(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-md h-24 resize-vertical focus:ring-indigo-500 focus:border-indigo-500" required />
                        </div>
                         <div>
                            <label htmlFor="medicalHistory" className="block text-sm font-medium text-gray-700 mb-1">Relevant Medical History (Optional)</label>
                            <textarea id="medicalHistory" placeholder="Mention any conditions relevant to the role (optional)" value={medicalHistory} onChange={(e) => setMedicalHistory(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-md h-16 resize-vertical focus:ring-indigo-500 focus:border-indigo-500" />
                        </div>
                    </fieldset>

                    {/* Document Upload Section */}
                    <fieldset className="space-y-4 pt-4">
                        <legend className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Document Uploads (Max {MAX_FILE_SIZE_MB}MB per file, PDF/JPG/PNG only) *</legend>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                             <div>
                                <label htmlFor="idProof" className="block p-4 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-indigo-500 hover:bg-gray-50 text-center">
                                    <span className="block font-medium text-gray-700">Upload ID Proof (Aadhaar/PAN) *</span>
                                    <input id="idProof" type="file" onChange={(e) => handleFileChange(e, setIdProof)} className="hidden" accept=".pdf,.jpg,.jpeg,.png" required />
                                    {idProof ? (
                                        <span className="block text-green-600 mt-2 truncate text-xs">{idProof.name}</span>
                                    ) : (
                                        <span className="block text-gray-500 mt-2 text-xs">Click to choose file</span>
                                    )}
                                </label>
                            </div>
                             <div>
                                <label htmlFor="qualDoc" className="block p-4 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-indigo-500 hover:bg-gray-50 text-center">
                                    <span className="block font-medium text-gray-700">Upload Qualification Proof *</span>
                                    <input id="qualDoc" type="file" onChange={(e) => handleFileChange(e, setQualDoc)} className="hidden" accept=".pdf,.jpg,.jpeg,.png" required />
                                     {qualDoc ? (
                                        <span className="block text-green-600 mt-2 truncate text-xs">{qualDoc.name}</span>
                                    ) : (
                                        <span className="block text-gray-500 mt-2 text-xs">Click to choose file</span>
                                    )}
                                </label>
                            </div>
                        </div>
                    </fieldset>

                    {/* Submission Button */}
                    <button type="submit" disabled={loading || success} // Disable after successful submission too
                        className="w-full bg-indigo-600 text-white py-3 mt-6 rounded-md font-bold text-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
                        {loading ? "Submitting..." : (success ? "Submitted!" : "Submit Application for Review")}
                    </button>
                </form>

                <p className="text-center mt-6 text-xs text-gray-500">
                    <span className="font-semibold">Note:</span> All applications are manually reviewed. You will be contacted via email regarding the status. Ensure your contact details are accurate.
                </p>
            </div>
        </div>
    );
}
